"use client";

import { readCookie } from "@/lib/cookies";

const CSRF_COOKIE = "sws.csrf-token";
const CSRF_HEADER = "x-csrf-token";
const MUTATION_METHODS = new Set(["POST", "PATCH", "PUT", "DELETE"]);

let patched = false;
let edgeBlockTriggered = false;

// Mọi API nội bộ luôn trả JSON. Nếu 1 request /api/* trả về text/html, response đó
// CÓ THỂ đến từ 1 lớp proxy/edge đứng trước (Cloudflare rate-limit "Error 1015",
// WAF block, v.v) — nhưng cũng có thể là route handler crash không bắt exception
// (Next.js tự render trang lỗi HTML mặc định) hoặc lỗi 502/504 từ reverse proxy nội
// bộ. Chỉ dựa vào content-type KHÔNG đủ để phân biệt 2 trường hợp — phải kèm header
// `cf-ray` (Cloudflare luôn gắn header này vào MỌI response nó xử lý, kể cả trang
// chặn của chính nó), nếu không sẽ hiểu nhầm app đang crash thật thành "bị Cloudflare
// chặn", che mất lỗi thật cần fix.
// Trường hợp thật sự bị chặn KHÔNG được để next-auth/code khác coi là "unauthenticated"
// hay lỗi API bình thường (gây redirect loop /login <-> / khi SessionProvider hiểu
// nhầm request bị chặn là mất session — xem session-watcher.tsx). Điều hướng sang
// /rate-limited (trang tự thiết kế) thay vì thẳng tới URL bị chặn — tránh lộ URL API
// thô/trang Cloudflare gốc ra ngoài (không chuyên nghiệp).
function isEdgeBlockResponse(res: Response): boolean {
  const contentType = res.headers.get("content-type") ?? "";
  return contentType.includes("text/html") && res.headers.has("cf-ray");
}

// Gắn header x-csrf-token vào mọi fetch mutation cùng-origin tới /api/*, đọc giá trị
// từ cookie sws.csrf-token (set bởi middleware.ts). Patch 1 lần cho window.fetch thay
// vì sửa từng chỗ gọi API rải rác trong app — double-submit cookie CSRF (xem middleware.ts).
export function installCsrfFetch() {
  if (patched || typeof window === "undefined") return;
  patched = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
    const url = typeof input === "string" ? input
      : input instanceof URL ? input.toString()
      : input.url;
    const isSameOriginApi = url.startsWith("/api/") || url.startsWith(`${window.location.origin}/api/`);

    let finalInit = init;
    if (MUTATION_METHODS.has(method) && isSameOriginApi) {
      const token = readCookie(CSRF_COOKIE);
      if (token) {
        const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
        headers.set(CSRF_HEADER, token);
        finalInit = { ...init, headers };
      }
    }

    const res = await originalFetch(input, finalInit);

    if (isSameOriginApi && isEdgeBlockResponse(res)) {
      if (!edgeBlockTriggered) {
        edgeBlockTriggered = true;
        window.location.href = "/rate-limited";
      }
      return new Promise<Response>(() => {}); // giữ caller treo — trang sắp unload
    }

    return res;
  };
}
